import React, { ReactNode, useEffect, useState } from "react";
import {
    AppLayout,
    ContentLayout,
    Flashbar,
    Header,
    Link,
    SideNavigation,
    SideNavigationProps,
} from "@cloudscape-design/components";
import { I18nProvider } from "@cloudscape-design/components/i18n";
import cloudscapeMessages from "@cloudscape-design/components/i18n/messages/all.en";
import { Outlet, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    setSideNavOpen,
    selectPageTitle,
    selectSideNavOpen,
    selectInfoMessages,
    removeInfoMessage
} from "src/core/redux";
import { useAppDispatch } from "src/core/redux/hooks";
import { SentinelDefaultInfoSection } from "./help/default-info-section";
import { SentinelOnboardingPageInfoSection } from "./help/onboarding-page-info-section";
import { SentinelDryRunInfoSection } from "./help/dry-run-info-section";
import { SentinelLeadershipDashboardInfoSection } from "./help/leadership-dashboard-info-section";
import { LEADERSHIP_NAV_TITLE, LEADERSHIP_PAGE_TITLE } from "src/web/constants";
const LOCALE = "en";
const menuItems = [
    {
        type: "link",
        text: "Overview",
        href: "/#/",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Dry Run Mode",
        href: "/#/dry-run",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: LEADERSHIP_NAV_TITLE,
        href: "/#/leadership-dashboard",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Onboard Pipelines",
        href: "/#/onboard",
    } as SideNavigationProps.Item,
    {
        type: "link",
        text: "Teams",
        href: "/#/team",
    } as SideNavigationProps.Item,
];
const sideNavMenuItems = menuItems;
sideNavMenuItems.push({
    type: "link",
    text: "Contact Us",
    href: "https://tiny.amazon.com/19sx7grly",
    external: true,
} as SideNavigationProps.Item);
export const SentinelLayout = () => {
    const pageTitle = useSelector(selectPageTitle);
    const navOpen = useSelector(selectSideNavOpen);
    const infoMessages = useSelector(selectInfoMessages);
    const dispatch = useAppDispatch();
    const [messages, setMessages] = useState<typeof infoMessages>([]);

    const [toolsOpen, setToolsOpen] = useState(false);
    const [infoSection, setInfoSection] = useState<React.ReactNode>(() => (
        <SentinelDefaultInfoSection/>
    ));

    useEffect(() => {
        const mappedMessages = infoMessages.map(message => ({
            ...message,
            onDismiss: () => dispatch(removeInfoMessage(message))
        }));
        setMessages(mappedMessages);
    }, [infoMessages]);

    const loadContentBasedOnPageTitle = (pageTitle: string) => {
        setToolsOpen(true);
      
        setInfoSection(
            (() => {
                switch (pageTitle) {
                case "Onboarding Page":
                    return <SentinelOnboardingPageInfoSection />;
                case "Dry Run Mode":
                    return <SentinelDryRunInfoSection />;
                case LEADERSHIP_PAGE_TITLE:
                    return <SentinelLeadershipDashboardInfoSection />;
                default:
                    return <SentinelDefaultInfoSection />;
                }
            })()
        );
    };
      

    return (
        <I18nProvider locale={LOCALE} messages={[cloudscapeMessages]}>
            <AppLayout
                navigationOpen={navOpen}
                navigation={
                    <SideNavigation
                        header={{
                            href: "#",
                            text: "Dependency Sentinel",
                        }}
                        items={sideNavMenuItems}
                    />
                }
                notifications={
                    <Flashbar
                        items={messages}
                    />
                }
                onNavigationChange={(e) => dispatch(setSideNavOpen(e.detail.open))}
                onToolsChange={({ detail }) => setToolsOpen(detail.open)}
                toolsOpen={toolsOpen}
                tools={infoSection}
                content={
                    <ContentLayout
                        header={
                            <Header variant="h1" info={
                                <Link
                                    variant="info"
                                    onFollow={() =>
                                        loadContentBasedOnPageTitle(pageTitle)
                                    }
                                >
                                Info
                                </Link>}>
                                {pageTitle}
                            </Header>
                        }
                    >
                        <Outlet context={[infoSection, setInfoSection, toolsOpen, setToolsOpen]}/>
                    </ContentLayout>
                }
            />
        </I18nProvider>
    );
};
export default SentinelLayout;

export function useInfoSection() {
    return useOutletContext<
      [
        ReactNode,
        React.Dispatch<React.SetStateAction<React.ReactNode>>, // Set the helpInfoSection panel with content
        boolean, // Open and close panel
        React.Dispatch<React.SetStateAction<boolean>> // Set the status to set the visibility
      ]
    >();
}